import { ExamResultType, Question, QuestionAnswer, QuestionStatus, QuestionType } from '@core/types';
import { Button, Close, HighFive, Loader, SurveyStart, Warning } from '@core/ui';
import {
    nextQuestion,
    previousQuestion,
    refreshQuestionnaireResult,
    retryQuestionnaire,
    stopQuestionnaire,
    useAppDispatch,
    useAppSelector
} from '@online-webinar/store';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { QuestionnaireMultipleChoiceQuestion, QuestionnaireOpenQuestion, QuestionnaireSingleChoiceQuestion } from '../../molecules';

interface Props {
    onClose: (type: 'survey' | 'exam') => void;
}

export const QuestionnaireOverlay = ({ onClose }: Props) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const type = useAppSelector((state) => state.questionnaire.type);
    const title = useAppSelector((state) => state.questionnaire.title);
    const intro = useAppSelector((state) => state.questionnaire.intro);
    const outro = useAppSelector((state) => state.questionnaire.outro);
    const passed = useAppSelector((state) => state.questionnaire.passed);
    const failed = useAppSelector((state) => state.questionnaire.failed);
    const required = useAppSelector((state) => state.questionnaire.required);
    const questions = useAppSelector((state) => state.questionnaire.questions);
    const question = useAppSelector((state) => state.questionnaire.question);
    const questionIndex = useAppSelector((state) => state.questionnaire.questionIndex) || 0;
    const givenAnswer = useAppSelector((state) => state.questionnaire.answer);
    const showIsRequired = useAppSelector((state) => state.questionnaire.showIsRequired);
    const status = useAppSelector((state) => state.questionnaire.status);
    const questionnaireResult = useAppSelector((state) => state.questionnaire.questionnaireResult);
    const [wrongQuestions, setWrongQuestions] = useState<Question[]>([]);

    const [answer, setAnswer] = useState<QuestionAnswer>(null!);

    useEffect(() => {
        if (status === QuestionStatus.ShowOutro) {
            dispatch(refreshQuestionnaireResult());
        }
    }, [status, type]);

    useEffect(() => {
        return () => {
            // Make sure to stop the questionnaire when leaving, otherwise the state will remain if the user returns to the webinar
            dispatch(stopQuestionnaire());
        };
    }, []);

    useEffect(() => {
        if (!questionnaireResult || !questionnaireResult.wrongAnswers || !questions) {
            setWrongQuestions([]);
            return;
        }

        const findWrongQuestions = (wrongAnswers: QuestionAnswer[]): Question[] => {
            if (!questions || questions.length === 0) {
                return [];
            }
            return questions.filter((q) => wrongAnswers.some((wa) => wa.questionId === q.questionId));
        };
        setWrongQuestions(findWrongQuestions(questionnaireResult.wrongAnswers));
    }, [questionnaireResult]);

    const closeQuestionnaire = () => {
        // First close, then stop
        onClose(type || 'exam');
        dispatch(stopQuestionnaire());
    };

    if (!type || !questions) {
        return <></>;
    }

    return (
        <div className="col-span-full row-span-full bg-linear-to-b from-primary/95 to-primary/75 flex py-[clamp(.5rem,-.25rem_+_4dvh,2rem)] z-60">
            <div
                className={`relative w-full ${type === 'exam' ? 'max-w-6xl h-full min-h-80' : 'max-w-lg min-h-80 self-center'} max-h-full bg-white rounded-lg flex justify-center mx-auto overflow-auto`}
            >
                {!required && (
                    <div className="absolute top-2 right-2">
                        <Button iconbtn onClick={() => closeQuestionnaire()}>
                            <Close classNames="size-6 stroke-current" />
                        </Button>
                    </div>
                )}

                <div className="w-full max-w-3xl flex flex-col px-4 py-3 md:px-6 md:py-4 lg:px-8 lg:py-6">
                    <h2 className={`text-2xl text-center ${type === 'exam' ? 'mb-8 md:mb-10' : 'mb-4 md:mb-6'} px-9`}>{title}</h2>
                    {status === QuestionStatus.ShowIntro && (
                        <>
                            {type === 'exam' ? (
                                <div className="relative border-b-2 border-gray-300 text-sm mb-6 pb-1">{t('Questionnaire.Preface')}</div>
                            ) : (
                                <div className="relative mx-auto mb-6 after:absolute after:top-2 after:left-2 after:w-full after:h-full after:bg-accent-light after:rounded-2xl">
                                    <SurveyStart classNames="text-accent-light stroke-primary z-10" />
                                </div>
                            )}
                            <div className={`mb-6 ${type === 'survey' ? 'text-center' : ''}`}>
                                <Markdown>{intro}</Markdown>
                            </div>
                            <div className="mt-auto flex justify-end">
                                <Button classNames="shrink basis-[calc(50%-.5rem)]" onClick={() => dispatch(nextQuestion(null))} variant="primary">
                                    {t('Questionnaire.Button.Next')}
                                </Button>
                            </div>
                        </>
                    )}
                    {status === QuestionStatus.ShowOutro && (
                        <>
                            {type === 'exam' ? (
                                <div className="border-b-2 border-gray-300 text-sm mb-6">{t('Questionnaire.Result')}</div>
                            ) : (
                                <div className="relative mx-auto mb-6 after:absolute after:top-2 after:left-2 after:w-full after:h-full after:bg-accent-light after:rounded-2xl">
                                    <HighFive classNames="text-accent-light stroke-primary z-10" />
                                </div>
                            )}
                            {questionnaireResult ? (
                                <>
                                    {questionnaireResult.result === ExamResultType.NotAvailable && (
                                        <div className="mb-6">
                                            <Markdown>{outro}</Markdown>
                                        </div>
                                    )}
                                    {questionnaireResult.result === ExamResultType.Passed && (
                                        <div className="mb-6">
                                            <Markdown>
                                                {passed ||
                                                    t('Questionnaire.Exam.Passed', {
                                                        score: questionnaireResult.score,
                                                        passingScore: questionnaireResult.passingScore
                                                    })}
                                            </Markdown>
                                        </div>
                                    )}
                                    {questionnaireResult.result === ExamResultType.Failed && (
                                        <>
                                            <div className="mb-6">
                                                <Markdown>
                                                    {failed ||
                                                        t('Questionnaire.Exam.Failed', {
                                                            score: questionnaireResult.score,
                                                            passingScore: questionnaireResult.passingScore
                                                        })}
                                                </Markdown>
                                            </div>
                                            {wrongQuestions.length > 0 && (
                                                <div className="mb-6">
                                                    <p className="flex mb-4">
                                                        <Warning classNames="size-6 flex-none me-2 lg:me-4" />
                                                        {t('Questionnaire.Exam.WrongAnswers', { amount: wrongQuestions.length })}
                                                    </p>
                                                    <ul>
                                                        {wrongQuestions.map((a) => (
                                                            <li
                                                                key={a.questionId}
                                                                className="relative ps-8 lg:ps-10 mb-4 before:absolute before:top-[calc(1.25rem-10px)] before:left-[calc(.75rem-5px)] before:size-[10px] before:rounded-full before:border-2 before:border-warning after:absolute after:top-5 after:bottom-0 after:left-[calc(.75rem-1px)] after:w-2px after:bg-warning"
                                                            >
                                                                <p className="mb-1">{a.questionText}</p>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <div className="flex gap-4 mt-auto">
                                        {type === 'survey' && (
                                            <Button
                                                classNames="grow shrink basis-[calc(50%-.5rem)]"
                                                onClick={() => dispatch(previousQuestion(answer))}
                                                variant="primary-outline"
                                            >
                                                {t('Questionnaire.Button.Previous')}
                                            </Button>
                                        )}
                                        {type === 'exam' && questionnaireResult.result === ExamResultType.Failed && questionnaireResult.canRetry && (
                                            <Button
                                                classNames="grow shrink basis-[calc(50%-.5rem)]"
                                                onClick={() => dispatch(retryQuestionnaire())}
                                                variant="primary-outline"
                                            >
                                                {t('Questionnaire.Button.Retry')}
                                            </Button>
                                        )}
                                        <Button classNames="grow shrink basis-[calc(50%-.5rem)]" onClick={closeQuestionnaire} variant="primary">
                                            {t('Questionnaire.Button.End')}
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="mb-4">{t('Questionnaire.ResultLoading')}</p>
                                    <Loader classNames="mx-auto mb-6" />
                                </>
                            )}
                        </>
                    )}
                    {status === QuestionStatus.ShowQuestion && question && (
                        <>
                            <div className="border-b-2 border-gray-300 text-sm mb-6">
                                {t('Questionnaire.Progress', { number: questionIndex + 1, total: questions.length })}
                                <div
                                    className="h-2px bg-primary translate-y-full"
                                    style={{ width: `${((questionIndex + 1) / questions.length) * 100}%` }}
                                ></div>
                            </div>
                            <div className={`mb-6 py-2 ${showIsRequired && 'border-2 border-warning rounded-md -mx-[calc(.5rem+2px)] -my-2px px-2 pb-5'}`}>
                                <h3 className="text-xl mb-2">{question.questionText}</h3>
                                {question.questionType === QuestionType.Open && (
                                    <QuestionnaireOpenQuestion question={question} answer={givenAnswer || null} answerUpdated={(a) => setAnswer(a)} />
                                )}
                                {question.questionType === QuestionType.SingleChoice && (
                                    <QuestionnaireSingleChoiceQuestion
                                        {...(type === 'exam' && {
                                            classNames:
                                                'border border-gray-200 rounded-lg has-checked:border-primary has-checked:inset-shadow-[0_0_0_1px_hsl(var(--c-primary))] not-last:mb-1 ps-10 py-2 pe-4'
                                        })}
                                        question={question}
                                        answer={givenAnswer || null}
                                        answerUpdated={(a) => setAnswer(a)}
                                    />
                                )}
                                {question.questionType === QuestionType.MultipleChoice && (
                                    <QuestionnaireMultipleChoiceQuestion
                                        {...(type === 'exam' && {
                                            classNames:
                                                'border border-gray-200 rounded-lg has-checked:border-primary has-checked:inset-shadow-[0_0_0_1px_hsl(var(--c-primary))] not-last:mb-1 ps-10 py-2 pe-4'
                                        })}
                                        question={question}
                                        answer={givenAnswer || null}
                                        answerUpdated={(a) => setAnswer(a)}
                                    />
                                )}
                            </div>
                            {showIsRequired && (
                                <p className="self-start bg-white text-warning -mt-10 mb-4 -ml-1 pl-1 pr-2">
                                    <Warning classNames="size-6 fill-white float-left mr-1" />
                                    <span className="inline-block">{t('Questionnaire.Question.Required')}</span>
                                </p>
                            )}
                            <div className="flex flex-wrap gap-4 mt-auto">
                                <Button
                                    classNames="grow shrink basis-[calc(50%-.5rem)]"
                                    onClick={() => dispatch(previousQuestion(answer))}
                                    variant="primary-outline"
                                >
                                    {t('Questionnaire.Button.Previous')}
                                </Button>
                                <Button classNames="grow shrink basis-[calc(50%-.5rem)]" onClick={() => dispatch(nextQuestion(answer))} variant="primary">
                                    {t('Questionnaire.Button.Next')}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
